// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useEffect } from 'react';
import { useAuth } from './useAuth';
import { personaForRole, usePersona } from './usePersona';
import { useShellStore } from '../stores/shellStore';

/** Sentinel selectedSiteId meaning "the whole fleet" (managers/admins only). */
export const FLEET_ID = '__fleet__';

/** True when `selectedSiteId` names a real site (not unset, not the fleet sentinel). */
export const hasSelectedSite = (selectedSiteId: string | null | undefined): boolean =>
  selectedSiteId != null && selectedSiteId !== FLEET_ID;

export interface SiteOption {
  siteId: string;
  siteKey: string;
  label: string;
  /** Street line only. */
  addressLine1?: string;
  /** IANA zone, e.g. "America/New_York". */
  timezone?: string;
}

export interface ActiveSite {
  /** The site the Site page scopes to (always a real site id, for links). */
  siteId: string;
  /** The active site's key — the human-facing short code (e.g. "site_ga10_atlanta"). */
  siteKey: string;
  /** Header label — the site name, or "Whole fleet" in fleet mode. */
  siteLabel: string;
  /** The active site's IANA zone; undefined in fleet mode or when the twin has none. */
  siteTimezone?: string;
  /** Real sites the user may view (from OMS / the /me response). */
  options: SiteOption[];
  /** True when the user has more than one assigned site and may switch. */
  canSwitch: boolean;
  /** True when the persona may pick the whole fleet (Ops Manager / Admin). */
  canFleet: boolean;
  /** True when the manager has chosen the whole fleet rather than one site. */
  isFleet: boolean;
  /** Select a specific site. */
  setSite: (_siteId: string) => void;
  /** Switch to the whole-fleet view (managers/admins; no-op otherwise). */
  setFleet: () => void;
}

/**
 * Resolves the active site + whether the user may switch it.
 *
 * - **Any user with more than one assigned site** may switch between their
 *   sites (`canSwitch`). A user with a single site sees a static indicator.
 * - **Ops Manager / Admin** (persona `manager`) may additionally pick the
 *   **whole fleet** (`canFleet`), which is their default. A specific site
 *   drives the Site page; whole fleet swaps the Site rail item for Fleet.
 * - Operators/executives with multiple sites switch sites but have no fleet.
 * - Sites come from OMS / the /me response (`useAuth().sites`).
 */
/**
 * `reconcile: false` resolves the active site without ever writing to the
 * store. LaunchDarklyContext needs it: it sits above SiteRolloutRoster and
 * sees the full roster, so its idea of a valid selection differs from the
 * callers below the filter, and two writers that disagree rewrite each
 * other's value on every render.
 */
export function useActiveSite({ reconcile = true }: { reconcile?: boolean } = {}): ActiveSite {
  const { sites, effectiveRole, isLoading, sitesError } = useAuth();
  const { persona } = usePersona();
  const selectedSiteId = useShellStore(s => s.selectedSiteId);
  const setSelectedSite = useShellStore(s => s.setSelectedSite);

  const options: SiteOption[] = sites.map(s => ({
    siteId: s.site_id,
    siteKey: s.site_key,
    label: s.label,
    addressLine1: s.address?.addressLine1 ?? undefined,
    timezone: s.timezone ?? undefined,
  }));

  const defaultOption = options[0];

  const setSite = (siteId: string) => {
    const opt = options.find(o => o.siteId === siteId);
    setSelectedSite(siteId, opt?.label ?? siteId);
  };
  const setFleet = () => setSelectedSite(FLEET_ID, 'Whole fleet');

  const canSwitch = options.length > 1;
  const canFleet = persona === 'manager';

  const isFleet = canFleet && (selectedSiteId == null || selectedSiteId === FLEET_ID);
  const activeReal = options.find(o => o.siteId === selectedSiteId) ?? defaultOption;

  const preserveFleetSelection =
    personaForRole(effectiveRole) === 'manager' &&
    (selectedSiteId == null || selectedSiteId === FLEET_ID);
  const hasValidSelection =
    selectedSiteId != null && options.some(o => o.siteId === selectedSiteId);
  const staleSite =
    !isLoading &&
    !sitesError &&
    selectedSiteId != null &&
    selectedSiteId !== FLEET_ID &&
    !hasValidSelection;
  useEffect(() => {
    if (!reconcile) return;
    if (staleSite) {
      setSelectedSite(defaultOption?.siteId ?? null, defaultOption?.label ?? null);
    } else if (!canFleet && !preserveFleetSelection && defaultOption && !hasValidSelection) {
      setSelectedSite(defaultOption.siteId, defaultOption.label);
    }
  }, [
    reconcile,
    staleSite,
    canFleet,
    preserveFleetSelection,
    defaultOption,
    hasValidSelection,
    setSelectedSite,
  ]);

  return {
    siteId: activeReal?.siteId ?? '',
    siteKey: activeReal?.siteKey ?? '',
    siteLabel: isFleet ? 'Whole fleet' : (activeReal?.label ?? ''),
    siteTimezone: isFleet ? undefined : activeReal?.timezone,
    options,
    canSwitch,
    canFleet,
    isFleet,
    setSite,
    setFleet: canFleet ? setFleet : () => {},
  };
}
